import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import { ACTION_CANCEL_SUBMIT, ACTION_RETRACT, TIME_OFF_PAGE_TYPE } from "../util/constants";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_RETRACT_MESSAGE,
  UNKNOWN_ACTION_MESSAGE,
} from "../util/messageConstants";
import {
  validateEmployeeTORCancelRequest,
  validateEmployeeTORShiftStartSubmissionDeadline,
  validateEmployeeWOPDeadline,
} from "../util/timeOffRequestInputValidation";
import ConfirmButton from "./fundamentals/ConfirmButton";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import WarningDialog from "./fundamentals/WarningDialog";
import TimeOffRequestEmployeeActionConfirm from "./TimeOffRequestEmployeeActionConfirm";
import UnionContractCutoffTimeReminder from "./UnionContractCutoffTimeReminder";
import { isLWOPTOWOPRequest } from "../util/timeOffRequestUtils";

const TimeOffRequestEmployeeActionCheckDialog = (props) => {
  const userSession = useSelector(selectUserSession);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isActionError, setIsActionError] = useState(false);
  const [actionErrors, setActionErrors] = useState("");

  const [warningMessage, setWarningMessage] = useState("");

  useEffect(() => {
    isFormValidationSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFormValidationSuccess = () => {
    let validationMessage = "";

    validationMessage = validateEmployeeTORCancelRequest(
      props.timeOffRequest.timeOffRequestType,
      props.actionSelected,
      userSession.jobCode
    );

    if (validationMessage === "") {
      if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType)) {
        validationMessage = validateEmployeeWOPDeadline(
          props.timeOffRequest.timeOffRequestType,
          props.timeOffRequest.station,
          props.timeOffRequest.shiftStart,
          props.timeOffRequest.shiftEnd,
          props.actionSelected
        );
      } else
        validationMessage = validateEmployeeTORShiftStartSubmissionDeadline(
          props.timeOffRequest.shiftStart,
          props.actionSelected,
          props.timeOffRequest.station
        );
    }

    setWarningMessage(validationMessage);

    return validationMessage === "";
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    clearDialogState();
    props.onClickDialogClose(event, reason);
  };

  const clearDialogState = () => {
    setIsActionLoading(false);
    setIsActionError(false);
    setActionErrors("");
  };

  const handleSetIsActionError = (isActionError) => {
    setIsActionError(isActionError);
  };

  const handleSetActionErrors = (actionErrors) => {
    setActionErrors(actionErrors);
  };

  const handleSetIsActionLoading = (isActionLoading) => {
    setIsActionLoading(isActionLoading);
  };

  const handleSetIsActionSuccess = (isActionSuccess) => {
    if (isActionSuccess && props.onTimeOffRequestDialogClose) {
      props.onTimeOffRequestDialogClose();
    }
  };

  const renderCancelDialog = () => {
    return (
      <Grid container>
        <Grid item xs={12} className="action-check-dialog-question">
          {ARE_YOU_SURE_YOU_WANT_TO_CANCEL_MESSAGE}
        </Grid>
        <Grid item xs={12}>
          <Box mt={1} mb={1}>
            <UnionContractCutoffTimeReminder
              pageType={TIME_OFF_PAGE_TYPE}
              timeOffType={props.timeOffRequest.timeOffRequestType}
              isTimeOffRequestCancelAction={true}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderDialogDetails = () => {
    switch (props.actionSelected) {
      case ACTION_RETRACT:
        return ARE_YOU_SURE_YOU_WANT_TO_RETRACT_MESSAGE;
      case ACTION_CANCEL_SUBMIT:
        return renderCancelDialog();
      default:
        return UNKNOWN_ACTION_MESSAGE(props.actionSelected);
    }
  };

  return (
    <>
      {warningMessage !== "" ? (
        <WarningDialog
          isDialogOpen={props.isDialogOpen}
          handleClickDialogClose={handleClickDialogClose}
          header="Warning"
          message={warningMessage}
          severity={"error"}
        />
      ) : (
        <Dialog
          className="action-check-dialog"
          data-testid="action-check-dialog"
          fullWidth
          maxWidth="md"
          open={props.isDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
            Confirm
          </DialogTitle>
          <DialogContent className="action-check-dialog-content">
            <Grid container>
              <Grid item xs={12}>
                <DialogContentText component={"div"} id="alert-dialog-slide-description">
                  {renderDialogDetails()}
                  {isActionError ? <Alert severity="error">{actionErrors}</Alert> : ""}
                </DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className="action-check-dialog-buttons">
            <ConfirmButton
              variant="outlined"
              color="primary"
              onClick={handleClickDialogClose}
              isLoading={isActionLoading}
            >
              No
            </ConfirmButton>
            <TimeOffRequestEmployeeActionConfirm
              timeOffRequest={props.timeOffRequest}
              actionSelected={props.actionSelected}
              onClickDialogClose={handleClickDialogClose}
              onSetIsActionError={handleSetIsActionError}
              onSetActionErrors={handleSetActionErrors}
              onSetIsActionLoading={handleSetIsActionLoading}
              onSetIsActionSuccess={handleSetIsActionSuccess}
              isActionLoading={isActionLoading}
              isFormValidationSuccess={isFormValidationSuccess}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

TimeOffRequestEmployeeActionCheckDialog.propTypes = {
  timeOffRequest: PropTypes.object.isRequired,
  actionSelected: PropTypes.string.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  onTimeOffRequestDialogClose: PropTypes.func,
};

export default TimeOffRequestEmployeeActionCheckDialog;
